'use client';

import React, { createContext, useContext } from 'react';
import { usePathname } from 'next/navigation';
import { resolveBranch, setClientSiteHostConfig, siteUrlForBranch, type SiteHostConfig } from '@/lib/config/site-host';

interface SiteHostContextValue {
    /** Публичный хост запроса (x-public-host от proxy.js) */
    host: string;
    /** Филиал региона посетителя по гео-IP (x-geo-branch) или '' */
    geoBranch: string;
}

const SiteHostContext = createContext<SiteHostContextValue>({ host: '', geoBranch: '' });

/**
 * Передаёт клиентским компонентам конфиг хостов (env читается только на
 * сервере) и хост запроса. Конфиг кладётся в модуль site-host-env до рендера
 * детей, поэтому regionalizePath() в браузере работает так же, как на сервере.
 */
export function SiteHostProvider({ config, host, geoBranch = '', children }: { config: SiteHostConfig; host: string; geoBranch?: string; children: React.ReactNode }) {
    setClientSiteHostConfig(config);
    return <SiteHostContext.Provider value={{ host, geoBranch }}>{children}</SiteHostContext.Provider>;
}

/** Филиал региона посетителя по гео-IP ('' — не определён, робот или регион уже выбран). */
export function useGeoBranch(): string {
    return useContext(SiteHostContext).geoBranch;
}

/** Филиал и публичный путь текущей страницы (хост + путь, см. resolveBranch). */
export function useResolvedBranch() {
    const { host } = useContext(SiteHostContext);
    const pathname = usePathname() || '/';
    return resolveBranch({ host, pathname });
}

/** Код филиала текущей страницы: замена getBranchCodeFromPath(usePathname()). */
export function useBranchCode(): string {
    return useResolvedBranch().code;
}

/** Путь, который видит посетитель (на хосте филиала — без /geo/<code>). */
export function usePublicPathname(): string {
    return useResolvedBranch().publicPath;
}

/** Origin филиала текущей страницы — база для абсолютных ссылок в JSON-LD. */
export function useBranchSiteUrl(): string {
    return siteUrlForBranch(useBranchCode());
}
